import { useCallback, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

/**
 * Usage is similar as useState, but the value is stored in the URL
 * @param paramName - The name of the param
 * @param defaultValue - The default value of the param in case it is not set in the URL
 */
function useStateParam<T extends string | number>(
  paramName: string,
  defaultValue: T,
  isNumber?: T extends number ? true : false,
): [T, (value: string | number) => void] {
  const history = useHistory();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  let paramValue: string | number | null = params.get(paramName);

  const setParam = useCallback(
    (value: string | number) => {
      const params = new URLSearchParams(location.search);
      const valueString = value.toString();
      if (valueString === params.get(paramName)) {
        return;
      }
      params.set(paramName, valueString);
      history.push({
        pathname: location.pathname,
        search: params.toString(),
      });
    },
    [location.search, location.pathname, paramName, history],
  );

  if (paramValue == null) {
    setParam(defaultValue);
    paramValue = defaultValue.toString();
  }

  if (isNumber) {
    const numValue = parseInt(paramValue as string, 10);
    if (isNaN(numValue)) {
      setParam(defaultValue);
      paramValue = defaultValue.toString();
    } else {
      paramValue = numValue;
    }
  }

  return [paramValue as T, setParam];
}

export const usePageParam = () => useStateParam('page', 1, true);

export const SCROLL_TO_RESPONDENT_TABLE_PARAM = 'scroll_to';

export function setScrollToParam(history: ReturnType<typeof useHistory>, paramValue = true) {
  const location = history.location;
  const params = new URLSearchParams(location.search);
  if (paramValue) {
    params.set(SCROLL_TO_RESPONDENT_TABLE_PARAM, 'true');
  } else {
    params.delete(SCROLL_TO_RESPONDENT_TABLE_PARAM);
  }
  history.replace({ pathname: location.pathname, search: params.toString() });
}

export function useScrollToRespondentTable(respondentTableRef: React.RefObject<HTMLDivElement>) {
  const history = useHistory();
  useEffect(() => {
    const scrollTo = new URLSearchParams(history.location.search).get(SCROLL_TO_RESPONDENT_TABLE_PARAM);
    if (!!scrollTo && respondentTableRef.current) {
      respondentTableRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setScrollToParam(history, false);
    }
  }, [respondentTableRef, history]);
}
